"use client";

import React, { createContext, useCallback, useEffect, useRef } from "react";
import { useAuth } from "@clerk/nextjs";
import { useQueryClient } from "@tanstack/react-query";
import { wsService } from "./WebSocketService";
import { QUERIES } from "modules/common/query";
import type { Message } from "modules/chat/model/chat";
import type { ConnectionStatus, RealtimeEvent } from "./types";
import { invalidateBinderTradeMembershipQueries } from "modules/trade-proposals/utils/proposalAvailabilityCache";
import { reconcileChatMessage } from "modules/chat/utils/optimisticMessages";

interface WebSocketContextValue {
  status: ConnectionStatus;
  subscribe: (topic: string) => void;
  unsubscribe: (topic: string) => void;
  isConnected: boolean;
}

export const WebSocketContext = createContext<WebSocketContextValue | null>(
  null
);

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { isSignedIn, getToken } = useAuth();
  const queryClient = useQueryClient();
  const [status, setStatus] = React.useState<ConnectionStatus>("disconnected");

  // Clerk's getToken is recreated every render -- stabilize via ref
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const stableGetToken = useCallback(() => getTokenRef.current(), []);

  // Connect/disconnect based on auth state
  useEffect(() => {
    if (isSignedIn) {
      wsService.connect(stableGetToken);
    } else {
      wsService.disconnect();
    }
    return () => wsService.disconnect();
  }, [isSignedIn, stableGetToken]);

  // Track connection status
  useEffect(() => {
    return wsService.onStatus(setStatus);
  }, []);

  // The connection itself writes the viewer's active presence. Refresh every
  // avatar status query once after that write instead of making each avatar
  // independently invalidate its own copy.
  useEffect(() => {
    if (status !== "connected") return;
    void queryClient.invalidateQueries({
      queryKey: [QUERIES.USER_ACTIVITY_STATUS],
    });
  }, [queryClient, status]);

  // Map WS events to query invalidations
  useEffect(() => {
    const unsubs: (() => void)[] = [];

    unsubs.push(
      wsService.on("new_message", (event: RealtimeEvent) => {
        const chatId = event.topic.split(":")[1];
        const msg = event.payload as Message;

        if (msg?.id) {
          queryClient.setQueryData<Message[]>(
            [QUERIES.MESSAGES, chatId],
            (old) => {
              if (!old) return old;
              return reconcileChatMessage(old, msg);
            }
          );
        }

        queryClient.invalidateQueries({ queryKey: [QUERIES.CHATS] });
      })
    );

    unsubs.push(
      wsService.on("proposal_updated", (event: RealtimeEvent) => {
        const chatId = event.topic.split(":")[1];
        queryClient.invalidateQueries({
          queryKey: [QUERIES.TRADE_PROPOSAL_BY_CHAT, chatId],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERIES.MESSAGES, chatId],
        });
        queryClient.invalidateQueries({ queryKey: [QUERIES.CHATS] });
        queryClient.invalidateQueries({
          queryKey: [QUERIES.TRADE_ITEM_AVAILABILITY],
        });
        invalidateBinderTradeMembershipQueries(queryClient);
      })
    );

    unsubs.push(
      wsService.on("new_notification", () => {
        queryClient.invalidateQueries({
          queryKey: [QUERIES.NOTIFICATIONS],
        });
        queryClient.invalidateQueries({ queryKey: [QUERIES.CHATS] });
      })
    );

    unsubs.push(
      wsService.on("notification_updated", () => {
        queryClient.invalidateQueries({
          queryKey: [QUERIES.NOTIFICATIONS],
        });
      })
    );

    unsubs.push(
      wsService.onReset(() => {
        queryClient.invalidateQueries({ queryKey: [QUERIES.MESSAGES] });
        queryClient.invalidateQueries({
          queryKey: [QUERIES.TRADE_PROPOSAL_BY_CHAT],
        });
        queryClient.invalidateQueries({ queryKey: [QUERIES.CHATS] });
        queryClient.invalidateQueries({
          queryKey: [QUERIES.TRADE_ITEM_AVAILABILITY],
        });
        invalidateBinderTradeMembershipQueries(queryClient);
        queryClient.invalidateQueries({
          queryKey: [QUERIES.NOTIFICATIONS],
        });
      })
    );

    return () => unsubs.forEach((fn) => fn());
  }, [queryClient]);

  const subscribe = useCallback(
    (topic: string) => wsService.subscribe(topic),
    []
  );
  const unsubscribe = useCallback(
    (topic: string) => wsService.unsubscribe(topic),
    []
  );

  const value = React.useMemo<WebSocketContextValue>(
    () => ({
      status,
      subscribe,
      unsubscribe,
      isConnected: status === "connected",
    }),
    [status, subscribe, unsubscribe]
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}
